import { isGenericEnabled } from "@/lib/wallet/solana/allowlist";

/**
 * Wallets that present themselves as Phantom, read from their own extension
 * builds (2026-10-23): OKX and Bitget set `isPhantom` on their EVM providers,
 * and Exodus (with "prioritize" on) and Magic Eden can also take
 * `window.phantom.ethereum` and `window.phantom.solana` themselves.
 *
 * Each also sets a flag of its own, which is what tells them apart. The
 * extension versions those flags were read from were not recorded, so each is
 * marked unknown; re-check against a current build before relying on one:
 *
 * - `isExodus`: Exodus, version unknown.
 * - `isMagicEden`: Magic Eden, version unknown.
 * - `isOkxWallet`, `isOKExWallet`: OKX, version unknown (the second is the
 *   older OKEx name).
 * - `isBitKeep`: Bitget, version unknown. Bitget may also set
 *   `isBitgetWallet`; unverified, so not listed.
 *
 * A flag missing here fails open, to the bare `isPhantom` check.
 */
const IMPERSONATORS = [
  "isExodus",
  "isMagicEden",
  "isOkxWallet",
  "isOKExWallet",
  "isBitKeep",
];

/**
 * Whether `provider` is one of the wallets above passing for Phantom. A
 * property read and nothing more: every other question is a request, and a
 * request is what must not reach a wallet on page load.
 */
export function impersonatesPhantom(provider: unknown): boolean {
  if (typeof provider !== "object" || provider === null) return false;
  return IMPERSONATORS.some((flag) => Reflect.get(provider, flag) === true);
}

/**
 * Phantom itself: its own flag, and none of an impersonator's. Phantom's
 * docs name `isPhantom` as the way to detect it on both of its providers.
 */
export function isGenuinePhantom(provider: unknown): boolean {
  if (typeof provider !== "object" || provider === null) return false;
  return (
    Reflect.get(provider, "isPhantom") === true &&
    !impersonatesPhantom(provider)
  );
}

/**
 * The Phantom check for an injected EVM provider that this build applies:
 * `isGenuinePhantom` where the generic Solana layer runs, the bare flag
 * (main's) on a flag-unset build, which is production. The user's decision
 * (2026-10-26) keeps every EVM fix of this branch behind the flag.
 */
export function phantomInFront(provider: unknown): boolean {
  if (isGenericEnabled()) return isGenuinePhantom(provider);
  return (
    typeof provider === "object" &&
    provider !== null &&
    Reflect.get(provider, "isPhantom") === true
  );
}
